import { CustomError } from '@core/utils';
import { cache } from 'react';

import { UnauthApi } from '@/api';
import {
  DEFAULT_FESTIVAL_RADIUS_METER,
  NATIONWIDE_FESTIVAL_RADIUS_METER,
} from '@/constants/festival';
import { DEFAULT_LOCATION } from '@/constants/geo';
import { buildRegionMetaData } from '@/utils/buildRegionMetaData';

// 백엔드가 "매핑 자료 없음"을 200 + return_code 30404 로 응답하는 경우가 있어
// 리스트 wrapper는 빈 배열로 흡수한다.
const THEME_NOT_FOUND = 30404;

const isThemeNotFound = (error: unknown): boolean =>
  error instanceof CustomError && error.return_code === THEME_NOT_FOUND;

const getDefaultSubRegionCode = cache(async (regionCode: string) => {
  const { data } =
    await UnauthApi['get:/wello/v2/allows/wello-policy-code/all']();

  return buildRegionMetaData(
    data?.context?.contents ?? [],
  ).subRegionByParent.get(regionCode)?.[0]?.code;
});

interface GetThemeFestivalsOptions {
  page?: number;
  size?: number;
  searchRegionCode?: string;
  searchSubRegionCode?: string;
}

// 월 아래 첫 번째 칩으로 노출되는 하드코딩 "전체"의 식별자.
export const ALL_THEME_ID = 'all';

interface GetThemeFestivalsByRegionParams {
  themeId: string;
  searchRegionCode?: string;
  searchSubRegionCode?: string;
  page?: number;
  size?: number;
}

export const getMonthlyThemesContext = cache(async (date: string) => {
  const { data } = await UnauthApi['get:/wello/v1/allows/theme/monthly']({
    query: { date },
  });

  return data?.context;
});

export const getMonthlyThemes = async (date: string) => {
  const context = await getMonthlyThemesContext(date);

  return [...(context?.themes ?? [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
};

export type MonthlyTheme = Awaited<ReturnType<typeof getMonthlyThemes>>[number];

export const getThemeFestivals = async (
  themeId: string,
  subRegionCode: string,
  {
    page = 1,
    size = 20,
    searchRegionCode,
    searchSubRegionCode,
  }: GetThemeFestivalsOptions = {},
) => {
  try {
    const { data } = await UnauthApi[
      'get:/wello/v1/allows/theme/{themeId}/festival'
    ]({
      path: { themeId },
      query: {
        subRegionCode,
        page,
        size,
        ...(searchRegionCode ? { searchRegionCode } : {}),
        ...(searchSubRegionCode ? { searchSubRegionCode } : {}),
      },
    });

    return data?.context?.content ?? [];
  } catch (error) {
    if (isThemeNotFound(error)) return [];
    throw error;
  }
};

export type ThemeFestival = NonNullable<
  Awaited<ReturnType<typeof getThemeFestivals>>
>[number];

export const getThemeFestivalsByRegion = async ({
  themeId,
  searchRegionCode,
  searchSubRegionCode,
  page = 1,
  size = 20,
}: GetThemeFestivalsByRegionParams) => {
  const distanceSubRegionCode =
    searchSubRegionCode ??
    (searchRegionCode
      ? await getDefaultSubRegionCode(searchRegionCode)
      : undefined) ??
    DEFAULT_LOCATION.codeSubRegion;

  return getThemeFestivals(themeId, distanceSubRegionCode, {
    page,
    size,
    ...(searchRegionCode ? { searchRegionCode } : {}),
    ...(searchSubRegionCode ? { searchSubRegionCode } : {}),
  });
};

interface GetQueryFestivalsParams {
  date: string;
  searchRegionCode?: string;
  searchSubRegionCode?: string;
  page?: number;
  size?: number;
}

export const getQueryFestivals = async ({
  date,
  searchRegionCode,
  searchSubRegionCode,
  page = 1,
  size = 20,
}: GetQueryFestivalsParams): Promise<ThemeFestival[]> => {
  const distanceSubRegionCode =
    searchSubRegionCode ??
    (searchRegionCode
      ? await getDefaultSubRegionCode(searchRegionCode)
      : undefined) ??
    DEFAULT_LOCATION.codeSubRegion;

  const { data } = await UnauthApi['get:/wello/v2/allows/query/tour/festival']({
    query: {
      subRegionCode: distanceSubRegionCode,
      radius: searchSubRegionCode
        ? DEFAULT_FESTIVAL_RADIUS_METER
        : NATIONWIDE_FESTIVAL_RADIUS_METER,
      date,
      page,
      size,
      ...(searchRegionCode ? { searchRegionCode } : {}),
      ...(searchSubRegionCode ? { searchSubRegionCode } : {}),
    },
  });

  return data?.context?.content ?? [];
};

export const getThemeArticles = async (
  themeId: string,
  { page = 1, size = 20 }: { page?: number; size?: number } = {},
) => {
  try {
    const { data } = await UnauthApi[
      'get:/wello/v1/allows/theme/{themeId}/article'
    ]({ path: { themeId }, query: { page, size } });

    return data?.context?.content ?? [];
  } catch (error) {
    if (isThemeNotFound(error)) return [];
    throw error;
  }
};

export type ThemeArticle = Awaited<ReturnType<typeof getThemeArticles>>[number];

// 비공개(30410)·잘못된 tourId(10001)는 "없는 축제"이므로 error가 아니라 notFound로
// 흘려보낸다. undefined를 반환하면 소비 측 (!detail?.id) 가드가 404를 렌더한다.
const FESTIVAL_GONE = 30410;
const FESTIVAL_INVALID_ARGUMENT = 10001;

const isFestivalNotFound = (error: unknown): boolean =>
  error instanceof CustomError &&
  (error.return_code === FESTIVAL_GONE ||
    error.return_code === FESTIVAL_INVALID_ARGUMENT);

export const getFestivalDetailResponse = cache(async (tourId: string) => {
  try {
    const { data } = await UnauthApi[
      'get:/wello/v1/allows/tour/festival/{tourId}'
    ]({ path: { tourId } });

    return data;
  } catch (error) {
    if (isFestivalNotFound(error)) return undefined;
    throw error;
  }
});

export type FestivalDetailResponse = NonNullable<
  Awaited<ReturnType<typeof getFestivalDetailResponse>>
>;

export const getFestivalDetail = async (tourId: string) =>
  (await getFestivalDetailResponse(tourId))?.context;

export type FestivalDetail = NonNullable<
  Awaited<ReturnType<typeof getFestivalDetail>>
>;

export const REVIEW_PAGE_SIZE = 10;

export const getFestivalReviews = async (
  tourId: string,
  { page = 1, size = REVIEW_PAGE_SIZE }: { page?: number; size?: number } = {},
) => {
  const { data } = await UnauthApi[
    'get:/wello/v1/allows/tour/festival/{tourId}/review'
  ]({ path: { tourId }, query: { page, size } });

  return data;
};

export type FestivalReviewListResponse = NonNullable<
  Awaited<ReturnType<typeof getFestivalReviews>>
>;

export type FestivalReview = NonNullable<
  NonNullable<FestivalReviewListResponse['context']>['content']
>[number];
